/** @jsxImportSource @emotion/react */

import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { Box, Typography, useTheme } from '@mui/material'
import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { RootState } from '@app/redux/store'
import { Accordion } from '@app/components/Atoms/Accordion'
import { SortableList } from '@app/components/Atoms/Sortable'
import {
  DialogData,
  HazardDetail,
  HazardFormInput,
  HazardScenario,
} from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { HazardOptionState } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog/HazardLayersForm/HazardBox'
import {
  layerDataByHazardId,
  layerDataByHazardIdDetail,
} from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { Tab, Tabs } from '@app/components/Molecules/Tabs'
import { MoreInformationModal } from '@app/components/Organisms/Modals/MoreInformationModal'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { usePreferences } from '@app/hooks/usePreferences'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { HazardControls_Legacy } from './_Legacy/HazardControls_Legacy'
import { HazardControlFormElement } from './HazardControlFormElement'
import { HazardControlFormHeader } from './HazardControlFormHeader'
import {
  fauxAccordionHeader,
  hazardControlsColumn,
  hazardControlsRow,
  hazardDetailFormContainer,
  scenarioAccordionStyle,
  tabsStyle,
} from './HazardControls.styles'

export interface GenericKeyValues<T> {
  [key: string]: T
}

export interface HazardOptionStateByHazard {
  [key: string]: HazardOptionState
}

export interface AvailableObjects {
  value: string | number
  disabled?: boolean
}

export type CombinedHazardDetail = HazardDetail & layerDataByHazardIdDetail

/*
 * Given a hazardDetail, return the current scenario using layers
 */
export const getScenarioByHazardDetail = (
  layers: MapLayer[],
  hazardDetail: CombinedHazardDetail,
) => {
  if (hazardDetail.hazard_id)
    return layers.find(
      (layer) => layer.hazard_id === (hazardDetail as CombinedHazardDetail).hazard_id,
    )
  return layers.find((layer) => layer.hazard_title === hazardDetail.title)
}

export const HazardControls = ({
  layersData,
  scenarioByHazardId,
  hazardIds,
}: {
  layersData: DialogData | null
  scenarioByHazardId: layerDataByHazardId | undefined
  hazardIds: string[]
}) => {
  const { useFeatureFlag } = useFeatureFlags()
  const isNewHazardControlsEnabled = useFeatureFlag(FEATURE_FLAGS.NEW_HAZARD_CONTROLS)
  const { use_hazard_name } = usePreferences()
  const theme = useTheme()
  const { prepareAndUpdateLayers } = useLayers()
  const { layers } = useSelector((state: RootState) => state.riskMap)
  const [tabIndex, setTabIndex] = useState(0)
  const [tabOpenAccordionIndex, setTabOpenAccordionIndex] = useState<{ [key: string]: number }>({})

  const { hazardOptionState, setHazardOptionState } = useMap()

  const [lastUpdatedHazard, setLastUpdatedHazard] = useState(null as HazardDetail | null)
  const [informationDetailsModal, setInformationDetailsModal] = useState({
    isOpen: false,
    helpFileName: '',
  })
  const hazardMapLayers = useMemo(() => {
    return layers.filter((layer: MapLayer) => layer.layerType === 'hazard')
  }, [layers])

  const filteredLayersDataMemoKey = useMemo(() => {
    // used to control when to recalc filteredLayersData
    const hazardString = hazardMapLayers
      .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
      .toSorted()
      .join(',')
    const scenarioString = scenarioByHazardId
      ? Object.keys(scenarioByHazardId)
          .map((hId) => {
            const scenarioIds = scenarioByHazardId[hId].scenarios
              .map((scenario) => scenario.hazard_id ?? scenario.assetTag ?? scenario.type)
              .toSorted()
            return `${hId}:${scenarioIds.join('-')}`
          })
          .toSorted()
          .join(',')
      : ''
    return `${hazardString}|${scenarioString}`
  }, [hazardMapLayers, scenarioByHazardId])

  /**
   * Get the dialog layer data that matches the hazardMapLayers
   * VERY side effect heavy
   * Different returns depending on if hazard_id's are present
   */
  const filteredLayersData: { [key: string]: CombinedHazardDetail[] } = useMemo(() => {
    if (!layersData) return {}
    const hasHazardIds = layersData?.hazardDialogData.hazardDetails.some(
      (HazardDetail: HazardDetail) =>
        HazardDetail.scenarios.some((scenario: HazardScenario) => scenario.hazard_id),
    )

    if (hasHazardIds && scenarioByHazardId) {
      const hazardIdRes = Object.keys(scenarioByHazardId).reduce(
        (acc, hId) => {
          const foundCurrentScenario = scenarioByHazardId[hId].scenarios.find((scenario) => {
            return hazardMapLayers.some((layer: MapLayer) => {
              return layer.assetTag === scenario.assetTag
            })
          })

          if (foundCurrentScenario) {
            const hazardKey = scenarioByHazardId[hId].title
            if (!acc[hazardKey]) {
              acc[hazardKey] = []
            }
            acc[hazardKey].push({
              ...scenarioByHazardId[hId],
              title: `${scenarioByHazardId[hId].hazard_name}`,
              scenarios: scenarioByHazardId[hId].scenarios.filter(
                (scenario) => scenario.hazard_id === foundCurrentScenario?.hazard_id,
              ),
            })
          }

          return acc
        },
        {} as { [key: string]: CombinedHazardDetail[] },
      )
      return hazardIdRes
    }
    const res = layersData.hazardDialogData.hazardDetails.filter((hazardDetail: HazardDetail) => {
      // filter based on title
      return hazardMapLayers.some((layer: MapLayer) => {
        const comparison = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
        return hasHazardIds
          ? (layer?.hazard_id ?? '' === comparison)
          : layer.hazard_title === comparison
      })
    })
    const hazardIdRes = res.reduce(
      (acc, hazardDetail: HazardDetail) => {
        const hazardKey = hazardDetail.title
        if (!acc[hazardKey]) {
          acc[hazardKey] = []
        }
        acc[hazardKey].push(hazardDetail as CombinedHazardDetail)
        return acc
      },
      {} as { [key: string]: CombinedHazardDetail[] },
    )
    return hazardIdRes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLayersDataMemoKey])

  useEffect(() => {
    if (Object.keys(filteredLayersData).length !== Object.keys(tabOpenAccordionIndex).length) {
      setTabOpenAccordionIndex(
        Object.keys(filteredLayersData).reduce(
          (acc, key) => {
            acc[key] = 0
            return acc
          },
          {} as { [key: string]: number },
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLayersData])

  /**
   * Used for reactivity as a primitive
   */
  const hazardTitleString = useMemo(() => {
    const hazardString = hazardMapLayers
      .map((hazardDetail) => {
        return hazardDetail?.assetTag ?? hazardDetail.display_name
      })
      .join(', ')
    return hazardString
  }, [hazardMapLayers])

  type HazardOptionStateWithID = (HazardOptionState[number] & { id: string })[]
  const mapIdsToForm = (form?: HazardOptionState): HazardOptionStateWithID => {
    if (!form) return []
    return form.map((item) => {
      return {
        ...item,
        id: item.key,
      }
    })
  }
  const defaultSortFormItems = (formItems: HazardOptionStateByHazard) => {
    const forcedOrder = ['slr', 'year']
    const sortedItems: HazardOptionStateByHazard = {}

    Object.keys(formItems).forEach((key) => {
      sortedItems[key] = formItems[key].sort((a, b) => {
        const aIndex = forcedOrder.indexOf(a.key)
        const bIndex = forcedOrder.indexOf(b.key)
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        } else if (aIndex !== -1) {
          return -1
        } else if (bIndex !== -1) {
          return 1
        } else {
          return 0
        }
      })
    })

    return sortedItems
  }
  /**
   * Set all values to an initial state of '' unless it finds a scenario that matches the current hazardMapLayers
   */
  const initialHazardOptionStateFunc = (useDefault = false) => {
    const initialHazardOptionStateByHazard: HazardOptionStateByHazard =
      structuredClone(hazardOptionState)
    Object.values(filteredLayersData)
      .flatMap((detailList) => detailList)
      ?.forEach((hazardDetail) => {
        const hazardKey = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
        if (!initialHazardOptionStateByHazard[hazardKey]) {
          initialHazardOptionStateByHazard[hazardKey] = []
        }
        for (const formElement of hazardDetail.form) {
          if (hazardKey in initialHazardOptionStateByHazard) {
            // Determine the default value, i.e. the first available value
            let initialHazardOptionIndex = initialHazardOptionStateByHazard[hazardKey].findIndex(
              (option) => option.key === formElement.key,
            )
            const defaultValue = hazardDetail.availableParameterValues[formElement.key][0]
            if (initialHazardOptionIndex === -1) {
              initialHazardOptionStateByHazard[hazardKey].push({
                key: formElement.key,
                value: defaultValue,
              })
              initialHazardOptionIndex = initialHazardOptionStateByHazard[hazardKey].length - 1
            } else {
              initialHazardOptionStateByHazard[hazardKey][initialHazardOptionIndex].value =
                defaultValue
            }
            if (
              !useDefault &&
              hazardOptionState[hazardKey]?.[initialHazardOptionIndex]?.value !== undefined
            ) {
              initialHazardOptionStateByHazard[hazardKey][initialHazardOptionIndex] = {
                key: formElement.key,
                value: hazardOptionState[hazardKey][initialHazardOptionIndex].value,
              }
            }
          }
        }
      })
    return defaultSortFormItems(initialHazardOptionStateByHazard)
  }

  /**
   * find the scenario based on the hazardOptionState
   */
  const findScenario = (
    scenarios: HazardScenario[],
    hazardKey: string,
  ): HazardScenario | undefined => {
    if (!hazardOptionState[hazardKey]) return
    const res = scenarios.find((scenario: HazardScenario) => {
      const hazardOptionKeys = Object.values(hazardOptionState[hazardKey]).map((d) => d.key)
      const isAvailable = hazardOptionKeys.every((optionKey) => {
        const hazardOption = hazardOptionState[hazardKey].find((option) => option.key === optionKey)
        const optionValue = hazardOption?.value
        if (
          optionValue === '' ||
          optionValue === null ||
          optionValue === undefined ||
          optionValue === 'Not Specified'
        )
          return true

        return Object.keys(scenario.parameters).some((param) => {
          if (param === optionKey) {
            return scenario.parameters[param].toString() === optionValue.toString()
          }
        })
      })
      return isAvailable
    })

    return res
  }

  const hasMultipleAvailableValues = (hazardDetail: HazardDetail) => {
    const parameterKeys = Object.keys(hazardDetail.scenarios[0]?.parameters || {})
    return parameterKeys.some((key) => {
      const uniqueValues = [
        ...new Set(hazardDetail.scenarios.map((scenario) => scenario.parameters[key])),
      ]
      return uniqueValues.length > 1
    })
  }

  const updateHazardOptionState = ({
    key,
    value,
    hazardDetail,
  }: {
    key: string
    value: string | number
    hazardDetail: CombinedHazardDetail | HazardDetail
  }) => {
    setHazardOptionState((prevState: HazardOptionStateByHazard) => {
      const hazardKey = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
      const optionState = prevState[hazardKey].map((option) => {
        if (option.key === key) {
          return {
            key,
            value,
          }
        }
        return option
      })
      return {
        ...prevState,
        [hazardKey]: optionState,
      }
    })
    setLastUpdatedHazard(hazardDetail)
  }

  /**
   * final function call for updating layers based on the hazard option state and findScenario
   * @TODO don't call if data is same
   */
  const updateLayersFromHazardOptionState = ({
    hazardDetail,
  }: {
    hazardDetail: CombinedHazardDetail | HazardDetail
  }) => {
    const allScenariosForHazard = scenarioByHazardId
      ? scenarioByHazardId[(hazardDetail as CombinedHazardDetail).hazard_id]?.scenarios
      : layersData?.hazardDialogData?.hazardDetails.find(
          (hazard) => hazard.title === hazardDetail.title,
        )?.scenarios
    let scenarios = hazardDetail.scenarios
    if (allScenariosForHazard) scenarios = allScenariosForHazard
    const newScenario = findScenario(
      scenarios,
      (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title,
    )
    if (!newScenario) return
    let scenarioTitle = ''

    // remove existing layer
    let newLayers = layers.filter((layer) => layer.hazard_title !== hazardDetail.title)
    // works because there is only one hazard_id per hazardDetail
    if (newScenario.hazard_id) {
      layersData?.hazardDialogData?.hazardDetails.find((hazard) => {
        return hazard.scenarios.some((scenario) => {
          if (scenario.hazard_id === newScenario.hazard_id) {
            scenarioTitle = hazard.title
            return true
          }
          return false
        })
      })
      newLayers = layers.filter((layer) => layer.hazard_id !== newScenario.hazard_id)
      // add new layer
      newLayers.push({
        ...newScenario,
        hazard_title: scenarioTitle,
        layerType: 'hazard',
        visible: true,
      })
      const layersSorted = layers
        .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
        .toSorted()
      const newLayersSorted = newLayers
        .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
        .toSorted()
      if (layersSorted.join() === newLayersSorted.join()) return
      prepareAndUpdateLayers({
        layers: newLayers,
        overwriteHazardParameters: true,
      })
      return
    }

    // add new layer
    newLayers.push({
      ...newScenario,
      hazard_title: hazardDetail.title,
      layerType: 'hazard',
      visible: true,
    })
    const layersSorted = layers.map((layer) => layer.assetTag ?? layer.type ?? layer.id).toSorted()
    const newLayersSorted = newLayers
      .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
      .toSorted()
    if (layersSorted.join() === newLayersSorted.join()) return
    prepareAndUpdateLayers({
      layers: newLayers,
      overwriteHazardParameters: true,
    })
  }

  /**
   * If changes are detected, it calls the updateLayersFromHazardOptionState function with the last updated hazard as the parameter.
   * This ensures that the layers are updated whenever there is a change in the hazard option state.
   */
  const updateLayersTimeout = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (lastUpdatedHazard === null) return
    if (updateLayersTimeout.current) clearTimeout(updateLayersTimeout.current)
    updateLayersTimeout.current = setTimeout(
      () => updateLayersFromHazardOptionState({ hazardDetail: lastUpdatedHazard }),
      200,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdatedHazard, hazardOptionState])

  // use a string cause it won't recalc on rerender unless it actually changed cause it's a primitive
  // used for initial setup as well as on hazard changes
  useEffect(() => {
    const initialHazardOptionState = initialHazardOptionStateFunc()
    setHazardOptionState((prev) => {
      return {
        ...prev,
        ...initialHazardOptionState,
      }
    })
    if (Object.keys(filteredLayersData).length < tabIndex + 1) {
      setTabIndex(Object.keys(filteredLayersData).length - 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardTitleString])

  if (!filteredLayersData) return null
  if (!Object.keys(hazardOptionState).length) return null

  const tabs: Tab[] = Object.keys(filteredLayersData).map((key) => {
    const hazardDetailList = filteredLayersData[key]
    const scenarioHasMultipleAvailableValues = hazardDetailList.reduce((acc, hazardDetail) => {
      acc.push(hasMultipleAvailableValues(hazardDetail))
      return acc
    }, [] as boolean[])
    return {
      label: key,
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '8px', gap: '2px' }}>
          {hazardDetailList.map(
            (hazardDetail: CombinedHazardDetail | HazardDetail, index: number) => {
              const mapLayer = getScenarioByHazardDetail(
                layers,
                hazardDetail as CombinedHazardDetail,
              )
              const componentKey = use_hazard_name.flag
                ? (mapLayer?.hazard_name ?? mapLayer?.display_name)
                : mapLayer?.display_name
              if (!scenarioHasMultipleAvailableValues[index])
                return (
                  <Box key={componentKey} css={fauxAccordionHeader({ theme })}>
                    <HazardControlFormHeader hazardDetails={hazardDetail as CombinedHazardDetail} />
                  </Box>
                )
              const hazardKey =
                (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
              return (
                <Accordion
                  customStyle={scenarioAccordionStyle({ theme })}
                  level="h3"
                  key={componentKey}
                  header={
                    <HazardControlFormHeader hazardDetails={hazardDetail as CombinedHazardDetail} />
                  }
                  scrollMaxHeight="100%"
                  defaultExpandedOnce
                  isExpanded={tabOpenAccordionIndex[key] === index}
                  onToggle={(expanded: boolean) => {
                    setTabOpenAccordionIndex((prev) => ({
                      ...prev,
                      [key]: expanded ? index : -1,
                    }))
                  }}
                  accordionStateKey={(hazardDetail as CombinedHazardDetail).hazard_id}
                  body={
                    <Box css={hazardControlsColumn}>
                      <Box css={hazardControlsRow}>
                        <Box css={hazardDetailFormContainer(theme)}>
                          <SortableList
                            items={mapIdsToForm(hazardOptionState[hazardKey])}
                            onChange={(newOrder) => {
                              const newOptionState = hazardOptionState
                              newOptionState[hazardKey] = hazardOptionState[hazardKey].sort(
                                (a, b) =>
                                  newOrder.findIndex((item) => item.id === a.key) -
                                  newOrder.findIndex((item) => item.id === b.key),
                              )
                              setHazardOptionState(newOptionState)
                            }}
                            renderItem={(
                              hazardOption,
                              _index: number,
                              isDragging: boolean,
                              isActive: boolean,
                            ) => {
                              const formInput = hazardDetail.form.find(
                                (input) => input.key === hazardOption.id,
                              ) as HazardFormInput
                              if (
                                !hazardOptionState[
                                  (hazardDetail as CombinedHazardDetail)?.hazard_id ??
                                    hazardDetail.title
                                ]
                              )
                                return null
                              return (
                                <HazardControlFormElement
                                  hazardDetail={hazardDetail}
                                  formElement={formInput}
                                  isDragging={isDragging}
                                  isActive={isActive}
                                  hazardOptionState={
                                    hazardOptionState[
                                      (hazardDetail as CombinedHazardDetail)?.hazard_id ??
                                        hazardDetail.title
                                    ]
                                  }
                                  handleParameterChange={(key, value) =>
                                    updateHazardOptionState({ key, value, hazardDetail })
                                  }
                                />
                              )
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  }
                ></Accordion>
              )
            },
          )}
          {scenarioHasMultipleAvailableValues.some((x) => x) && (
            <Box css={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
              <Typography
                onClick={() => {
                  const initState = initialHazardOptionStateFunc(true)
                  const hazardIdsToReset = hazardDetailList.map(
                    (hazardDetail) => +hazardDetail.hazard_id,
                  )
                  // Reset all hazard option states to their default values
                  setHazardOptionState((prev) => {
                    return Object.entries(prev).reduce((acc, [hazardKey, options]) => {
                      const hazardDetail = initState[hazardKey]
                      acc[hazardKey] = hazardIdsToReset.includes(+hazardKey)
                        ? hazardDetail
                        : options
                      return acc
                    }, {} as HazardOptionStateByHazard)
                  })
                  // Optionally update layers for all hazards
                  Object.values(filteredLayersData)
                    .flat()
                    .forEach((hazardDetail) => {
                      updateLayersFromHazardOptionState({ hazardDetail })
                    })
                }}
                className="reset-filters"
              >
                Reset
              </Typography>
            </Box>
          )}
        </Box>
      ),
    }
  })

  if (!isNewHazardControlsEnabled)
    return (
      <HazardControls_Legacy
        layersData={layersData}
        scenarioByHazardId={scenarioByHazardId}
        hazardIds={hazardIds}
      />
    )
  return (
    <>
      <Tabs
        tabIndex={tabIndex}
        handleChangeTab={setTabIndex}
        tabs={tabs}
        css={tabsStyle({ theme })}
        leftAligned
      />
      {informationDetailsModal.isOpen &&
        createPortal(
          <MoreInformationModal
            baseURL={''}
            helpFileName={informationDetailsModal.helpFileName}
            isOpen={informationDetailsModal.isOpen}
            handleClose={() =>
              setInformationDetailsModal({
                isOpen: false,
                helpFileName: '',
              })
            }
          />,
          document.body,
        )}
    </>
  )
}
